import React, { useState, useEffect, useRef } from "react";
import { createLink } from "../../../utils";
import Icon from "../../Icon/Icon";
import { Link } from "../..";
import useDebounce from "../debounce";
import styles from "./SearchInput.module.sass";
import { useGlobalValue } from "../../../state/GlobalProvider";
const getOptions = (optionList, filter, i18n) => {
  const normalizedFilterValue = (filter || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const filterRegex = new RegExp(`.*${normalizedFilterValue.replace(/\*/g, "\\*").replace(/\s/gi, ".*")}.*`, "ig");
  const filteredOptions = filter ? optionList.filter(({
    name
  }) => {
    const normalizedName = i18n`${name}`.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    return normalizedName.match(filterRegex);
  }) : optionList;
  return filteredOptions;
};
const renderList = (options, proxy, params, select, i18n, searchTerm, termPart) => {
  const children = options.map((option, index) => {
    const link = createLink(undefined, proxy, option, params);
    const actionSuffix = index === 0 && termPart == 0 ? /*#__PURE__*/React.createElement("small", {
      className: styles["selection-action"]
    }, i18n`Press`, " ", /*#__PURE__*/React.createElement("span", null, "tab"), " ", i18n`to select`) : "";
    return /*#__PURE__*/React.createElement("li", {
      key: index,
      onClick: () => {
        select && select(option);
      }
    }, link ? /*#__PURE__*/React.createElement(Link, {
      to: link,
      label: option.name
    }) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("small", null, option.name), actionSuffix));
  });
  const label = searchTerm && searchTerm.length ? i18n`No data found` : "";
  return /*#__PURE__*/React.createElement("ul", null, children && children.length ? children : /*#__PURE__*/React.createElement("li", null, /*#__PURE__*/React.createElement("small", null, label)));
};
const SearchInput = props => {
  const {
    className,
    onFocus,
    onBlur,
    search: searchResults = {},
    items = []
  } = props;
  const {
    i18n,
    search,
    proxy,
    params
  } = useGlobalValue();
  const [debounce, setDebounce] = useState(600);
  const [focus, setFocus] = useState(false);
  const [termPart, setTermPart] = useState(0);
  const [terms, setTerms] = useState(["", ""]);
  const [selected, setSelected] = useState(null);
  const root = useRef(null);
  const input = useRef(null);
  let placeholder = i18n`Search...`;
  if (focus) {
    placeholder = termPart === 0 ? i18n`Select an entity` : i18n`Search by term`;
  }
  const debouncedSearchTerm = useDebounce(terms[termPart], debounce);
  const searchResult = termPart === 1 && searchResults.q === debouncedSearchTerm && searchResults.data ? searchResults.data : [];
  const options = termPart === 0 ? getOptions(items, terms[termPart], i18n) : searchResult.slice(0, 10);
  useEffect(() => {
    if (termPart === 1 && selected && debouncedSearchTerm) {
      search && search(debouncedSearchTerm, selected.type, params.projectId, params.fiscalYear, params.modulePrefix, params.importId);
    }
  }, [debouncedSearchTerm, search, params.projectId, params.fiscalYear, params.modulePrefix, selected, termPart]);
  useEffect(() => {
    const handleClick = e => {
      if (focus && (!root.current || !root.current.contains(e.target))) {
        e.preventDefault();
        e.stopPropagation();
        if (input.current) {
          input.current.blur();
        }
        setTimeout(() => {
          setFocus(false);
          onBlur && onBlur(e);
        }, 10);
      }
    };
    window.addEventListener("click", handleClick, true);
    window.addEventListener("touchstart", handleClick, true);
    return () => {
      window.removeEventListener("click", handleClick, true);
      window.removeEventListener("touchstart", handleClick, true);
    };
  }, [focus, onBlur]);
  const select = option => {
    const index = options.findIndex(opt => opt === option);
    if (index >= 0 && termPart === 0) {
      setSelected(options[index]);
      setTermPart(1);
      input.current && input.current.focus();
    } else {
      input.current && input.current.blur();
      setFocus(false);
      onBlur && onBlur({
        target: {}
      });
    }
  };
  return items && items.length ? /*#__PURE__*/React.createElement("div", {
    ref: root,
    className: `${styles["root"]} ${focus ? styles["is-focus"] : ""} ${className || ""}`
  }, /*#__PURE__*/React.createElement("label", {
    className: styles["input-container"]
  }, focus && selected && /*#__PURE__*/React.createElement("small", {
    className: styles["type"]
  }, i18n`${selected.name}`), /*#__PURE__*/React.createElement("input", {
    ref: input,
    className: styles["input"],
    type: "text",
    value: focus ? terms[termPart] : "",
    placeholder: placeholder,
    size: "10",
    onKeyDown: e => {
      if (e.key === "Enter") {
        setDebounce(0);
      } else {
        setDebounce(600);
      }
      if (e.key === "Tab") {
        e.preventDefault();
        e.stopPropagation();
      }
      if (termPart === 0 && focus && (e.key === "Tab" || e.key === "Done")) {
        if (options && options[0]) {
          setSelected(options[0]);
          setTermPart(1);
        }
      } else if (focus && e.key === "Backspace" && !terms[termPart]) {
        setSelected(null);
        setTermPart(0);
      }
    },
    onChange: e => {
      const nextTerms = [...terms];
      nextTerms[termPart] = e.target.value;
      setTerms(nextTerms);
    },
    onFocus: e => {
      setFocus(true);
      onFocus && onFocus(e);
    }
  }), /*#__PURE__*/React.createElement(Icon, {
    className: styles["icon"],
    name: "loupe"
  })), focus && /*#__PURE__*/React.createElement("div", {
    className: styles["options"]
  }, renderList(options, proxy, params, select, i18n, terms[termPart], termPart))) : null;
};
export default SearchInput;